// The Hub's Guardrails tab (spec 14 §4.2): the guardrail history as a DataTable
// with kind, agent and date filters, each row opening its run's state and the
// cards it proposed. It is the surface where a facility admin reviews
// guardrail trips (§1). `agents.activity.read` only; the server scopes the
// rows to the agents bound within the reader's memberships.

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { EmptyState, ErrorState } from "../../../../components/states";
import { formatTimestamp } from "../../../../lib/datetime";
import { requestIdOf } from "../../../../lib/request";
import { formatMinor } from "../../inventory/lib/cost";
import { useGuardrailPage, useRun, type GuardrailQuery } from "../hooks/agents-queries";
import {
  GUARDRAIL_KINDS,
  dayEndIso,
  dayStartIso,
  type AgentGuardrailEvent,
  type AgentListItem,
  type GuardrailKind,
} from "../lib/agents-lib";
import { agentName, outcomeText, problemText, toolName } from "../lib/labels";

export interface GuardrailFilters {
  readonly kind?: GuardrailKind | undefined;
  readonly agent?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

/** The one line a row says about its event, per kind. */
export function guardrailSummary(
  t: TFunction,
  event: AgentGuardrailEvent,
  language: string,
): string {
  switch (event.kind) {
    case "prompt_injection_suspected":
      return t("agents.guardrails.detail.pattern", { pattern: event.detail.matched_pattern });
    case "cap_action_exceeded":
      return t("agents.guardrails.detail.capCount", {
        current: event.detail.current_value.toLocaleString(language),
        cap: event.detail.cap_value.toLocaleString(language),
      });
    case "cap_spend_exceeded": {
      const { spend_minor: spend, cap_minor: cap, currency } = event.detail;
      if (spend !== undefined && cap !== undefined && currency !== undefined) {
        return t("agents.guardrails.detail.spend", {
          spend: formatMinor(spend, currency, language),
          cap: formatMinor(cap, currency, language),
        });
      }
      return t("agents.guardrails.detail.tool", { tool: toolName(t, event.detail.tool_code) });
    }
    case "cap_token_exceeded":
      return t("agents.guardrails.detail.tokens", {
        used: event.detail.used.toLocaleString(language),
        budget: event.detail.budget.toLocaleString(language),
      });
    case "schema_validation_failed":
      return `${t("agents.guardrails.detail.tool", { tool: toolName(t, event.detail.tool_code) })} · ${t(
        "agents.guardrails.detail.attempt",
        { attempt: event.detail.attempt },
      )}`;
    case "citation_missing":
      return "";
    case "run_limit_exceeded":
      return event.detail.limit === "turns"
        ? t("agents.guardrails.detail.turnLimit", {
            value: event.detail.value.toLocaleString(language),
          })
        : t("agents.guardrails.detail.wallClock", {
            value: event.detail.value.toLocaleString(language),
          });
    default:
      return t("agents.guardrails.detail.tool", { tool: toolName(t, event.detail.tool_code) });
  }
}

function RunPanel({ runId }: { runId: string }) {
  const { t } = useTranslation();
  const run = useRun(runId, true);
  if (run.isPending) return <p className="ag-hint">{t("agents.common.loading")}</p>;
  if (run.isError) return <p className="ag-error">{t("agents.common.loadError")}</p>;
  const data = run.data;
  const outcome = outcomeText(t, data.outcome);
  return (
    <div className="ag-stack" data-testid="guardrail-run">
      <p className="ag-hint">
        {t("agents.journal.run.line", {
          trigger: t(`agents.trigger.${data.trigger_type}`),
          status: t(`agents.runStatus.${data.status}`),
        })}
        {outcome !== undefined ? ` · ${outcome}` : ""}
        {data.error !== null
          ? ` · ${t("agents.journal.run.error", { error: problemText(t, data.error.code) })}`
          : ""}
      </p>
      <p className="ag-hint">{t("agents.guardrails.runActions")}</p>
      {data.action_ids.length === 0 ? (
        <p className="ag-hint">{t("agents.guardrails.runNoActions")}</p>
      ) : (
        <ul className="ag-memberships">
          {data.action_ids.map((id) => (
            <li key={id}>
              <Link
                to="/agents/journal"
                search={{ action: id }}
                className="ag-link"
                data-testid="guardrail-open-card"
              >
                {t("agents.guardrails.openCard")}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function GuardrailsTab({
  agents,
  filters,
  onFilters,
}: {
  agents: readonly AgentListItem[];
  filters: GuardrailFilters;
  onFilters: (next: GuardrailFilters) => void;
}) {
  const { t, i18n } = useTranslation();
  const [cursors, setCursors] = useState<readonly (string | undefined)[]>([undefined]);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const cursor = cursors[cursors.length - 1];
  const query: GuardrailQuery = {
    kind: filters.kind,
    agent_id: filters.agent,
    from: filters.from !== undefined ? dayStartIso(filters.from) : undefined,
    to: filters.to !== undefined ? dayEndIso(filters.to) : undefined,
  };
  const page = useGuardrailPage(query, cursor);
  const byId = new Map(agents.map((a) => [a.id, a]));

  const setFilter = (next: GuardrailFilters) => {
    setCursors([undefined]);
    onFilters(next);
  };

  const columns: DataTableColumn<AgentGuardrailEvent>[] = [
    {
      key: "when",
      header: t("agents.guardrails.columns.when"),
      kind: "date",
      phonePriority: 2,
      render: (row) => formatTimestamp(row.created_at, i18n.language),
    },
    {
      key: "kind",
      header: t("agents.guardrails.columns.kind"),
      phonePriority: 1,
      render: (row) => t(`agents.guardrails.kind.${row.kind}`),
    },
    {
      key: "agent",
      header: t("agents.guardrails.columns.agent"),
      phonePriority: 3,
      render: (row) => {
        const agent = byId.get(row.agent_id);
        return agent === undefined ? "" : agentName(t, agent);
      },
    },
    {
      key: "detail",
      header: t("agents.guardrails.columns.detail"),
      render: (row) => guardrailSummary(t, row, i18n.language),
    },
    {
      key: "expand",
      header: t("agents.guardrails.expand"),
      actions: true,
      render: (row) =>
        row.run_id === null ? null : (
          <Button
            onClick={() => {
              setExpanded((prev) => {
                const next = new Set(prev);
                if (next.has(row.id)) next.delete(row.id);
                else next.add(row.id);
                return next;
              });
            }}
            aria-expanded={expanded.has(row.id)}
            data-testid="guardrail-expand"
          >
            {t("agents.guardrails.expand")}
          </Button>
        ),
    },
  ];

  const pageIndex = cursors.length;
  const rows = page.data?.items ?? [];

  return (
    <div className="ag-stack" data-testid="agents-guardrails">
      <div className="ag-filters" role="group" aria-label={t("agents.guardrails.title")}>
        <Field label={t("agents.guardrails.filters.kind")}>
          <Select
            value={filters.kind ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setFilter({
                ...filters,
                kind: value === "" ? undefined : (value as GuardrailKind),
              });
            }}
            data-testid="guardrail-filter-kind"
          >
            <option value="">{t("agents.common.all")}</option>
            {GUARDRAIL_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`agents.guardrails.kind.${kind}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("agents.guardrails.filters.agent")}>
          <Select
            value={filters.agent ?? ""}
            onChange={(e) => {
              setFilter({ ...filters, agent: e.target.value === "" ? undefined : e.target.value });
            }}
            data-testid="guardrail-filter-agent"
          >
            <option value="">{t("agents.common.all")}</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agentName(t, agent)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("agents.guardrails.filters.from")}>
          <input
            type="date"
            className="ds-input"
            value={filters.from ?? ""}
            onChange={(e) => {
              setFilter({ ...filters, from: e.target.value === "" ? undefined : e.target.value });
            }}
            data-testid="guardrail-filter-from"
          />
        </Field>
        <Field label={t("agents.guardrails.filters.to")}>
          <input
            type="date"
            className="ds-input"
            value={filters.to ?? ""}
            onChange={(e) => {
              setFilter({ ...filters, to: e.target.value === "" ? undefined : e.target.value });
            }}
            data-testid="guardrail-filter-to"
          />
        </Field>
        {filters.kind !== undefined ||
        filters.agent !== undefined ||
        filters.from !== undefined ||
        filters.to !== undefined ? (
          <Button
            onClick={() => {
              setFilter({});
            }}
          >
            {t("agents.guardrails.filters.clear")}
          </Button>
        ) : null}
      </div>
      {page.isError ? (
        <ErrorState
          title={t("agents.guardrails.loadError")}
          retryLabel={t("agents.common.retry")}
          onRetry={() => {
            void page.refetch();
          }}
          requestId={requestIdOf(page.error)}
          requestIdLabel={t("agents.common.requestId")}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowTestId="guardrail-row"
          loading={page.isPending}
          loadingLabel={t("agents.guardrails.loading")}
          empty={
            <EmptyState
              title={t("agents.guardrails.emptyTitle")}
              body={t("agents.guardrails.emptyBody")}
            />
          }
          expansion={(row) =>
            expanded.has(row.id) && row.run_id !== null ? <RunPanel runId={row.run_id} /> : null
          }
          expansionLabel={() => t("agents.guardrails.run")}
          pager={
            rows.length > 0 || pageIndex > 1
              ? {
                  range: t("agents.common.pageRange", {
                    from: (pageIndex - 1) * 25 + 1,
                    to: (pageIndex - 1) * 25 + rows.length,
                  }),
                  page: pageIndex,
                  onPage: (next) => {
                    if (next > pageIndex && page.data?.next_cursor) {
                      setCursors([...cursors, page.data.next_cursor]);
                    } else if (next < pageIndex) {
                      setCursors(cursors.slice(0, -1));
                    }
                  },
                  prevLabel: t("agents.common.previous"),
                  nextLabel: t("agents.common.next"),
                  hasPrev: pageIndex > 1,
                  hasNext: page.data?.next_cursor != null,
                }
              : undefined
          }
          data-testid="guardrail-table"
        />
      )}
    </div>
  );
}
